import { Trans } from "@lingui/macro";
import { useState } from "react";
import cx from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import { Tweet } from "react-tweet";
import { ArenaQuest } from "domain/arena";
import { useArena, ClaimOutcome } from "context/ArenaContext/ArenaContext";
import useWallet from "lib/wallets/useWallet";

const TELEGRAM_BOT_USERNAME = (import.meta as any).env?.VITE_TELEGRAM_BOT_USERNAME || "";

const BACKEND_URL =
  (import.meta as any).env?.VITE_BACKEND_URL || "http://localhost:4001";

const KIND_META: Record<ArenaQuest["kind"], { label: string; color: string }> = {
  DAILY_CHECKIN: { label: "Daily", color: "bg-green-500/15 text-green-500" },
  SOCIAL_TWITTER: { label: "Twitter", color: "bg-[#3FB5FF]/15 text-[#3FB5FF]" },
  SOCIAL_TELEGRAM: { label: "Telegram", color: "bg-[#3FB5FF]/15 text-[#3FB5FF]" },
  TIME_ATTACK: { label: "Time Attack", color: "bg-[#FFB347]/15 text-[#FFB347]" },
  LEVEL_UP: { label: "Level Up", color: "bg-[#FF6A88]/15 text-[#FF6A88]" },
  STAKING: { label: "Staking", color: "bg-[#00F5D4]/15 text-[#00F5D4]" },
};

/** Extract tweet ID from a twitter.com or x.com URL */
function extractTweetId(url?: string): string | null {
  if (!url) return null;
  const match = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
  return match ? match[1] : null;
}

export function QuestList() {
  const { quests, claimQuest, loading } = useArena();
  const twitter = quests.filter((q) => q.kind === "SOCIAL_TWITTER");
  const daily = quests.filter(
    (q) =>
      q.kind === "DAILY_CHECKIN" ||
      (q.kind.startsWith("SOCIAL_") && q.kind !== "SOCIAL_TWITTER")
  );
  const timed = quests.filter((q) => q.kind === "TIME_ATTACK");
  const levels = quests.filter((q) => q.kind === "LEVEL_UP");
  const staking = quests.filter((q) => q.kind === "STAKING");

  return (
    <div className="flex flex-col gap-20">
      <QuestGroup title={<Trans>Daily & Social</Trans>} quests={daily} onClaim={claimQuest} loading={loading} />
      <QuestGroup
        title={<Trans>X / Twitter</Trans>}
        quests={twitter}
        onClaim={claimQuest}
        loading={loading}
      />
      <QuestGroup title={<Trans>Staking</Trans>} quests={staking} onClaim={claimQuest} loading={loading} />
      <QuestGroup title={<Trans>Time-Attack</Trans>} quests={timed} onClaim={claimQuest} loading={loading} />
      <QuestGroup title={<Trans>Level-Up Milestones</Trans>} quests={levels} onClaim={claimQuest} loading={loading} />
    </div>
  );
}

function QuestGroup({
  title,
  quests,
  onClaim,
  loading,
  gridClassName,
}: {
  title: React.ReactNode;
  quests: ArenaQuest[];
  onClaim: (key: string, ctx?: any) => Promise<ClaimOutcome>;
  loading: boolean;
  gridClassName?: string;
}) {
  if (!quests.length) return null;
  return (
    <div>
      <h3 className="text-h2 mb-12 font-bold text-white">{title}</h3>
      <motion.div
        className={gridClassName || "grid gap-12 md:grid-cols-2 xl:grid-cols-3"}
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.06 } },
        }}
      >
        {quests.map((q) => (
          <QuestCard key={q.key} quest={q} onClaim={onClaim} loading={loading} />
        ))}
      </motion.div>
    </div>
  );
}

function QuestCard({
  quest,
  onClaim,
  loading,
}: {
  quest: ArenaQuest;
  onClaim: (key: string, ctx?: any) => Promise<ClaimOutcome>;
  loading: boolean;
}) {
  const meta = KIND_META[quest.kind];
  const { account } = useWallet();
  const { telegramLinked, twitterLinked, refreshTelegramLinked, refreshTwitterLinked } = useArena();
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<null | "success" | "error">(null);
  const isTelegram = quest.kind === "SOCIAL_TELEGRAM";
  const isTwitter = quest.kind === "SOCIAL_TWITTER";

  const tweetId = extractTweetId(quest.target?.tweetUrl);

  const handleClick = async () => {
    setBusy(true);
    try {
      let ctx: any;
      if (quest.kind === "SOCIAL_TWITTER") ctx = undefined; // backend uses stored TwitterLink
      if (quest.kind === "SOCIAL_TELEGRAM") ctx = undefined;
      const outcome = await onClaim(quest.key, ctx);
      setFlash(outcome.success ? "success" : "error");
      setTimeout(() => setFlash(null), 1200);
    } finally {
      setBusy(false);
    }
  };

  const handleSocialVerify = async () => {
    setBusy(true);
    try {
      if (isTelegram) await refreshTelegramLinked();
      if (isTwitter) await refreshTwitterLinked();
      const outcome = await onClaim(quest.key);
      setFlash(outcome.success ? "success" : "error");
      setTimeout(() => setFlash(null), 1200);
    } finally {
      setBusy(false);
    }
  };

  const isDaily = quest.kind === "DAILY_CHECKIN";
  const claimedToday = isDaily && quest.lastClaimedAt
    ? (Date.now() - new Date(quest.lastClaimedAt).getTime()) < 24 * 60 * 60 * 1000
    : false;
  const claimed = quest.status === "CLAIMED" || claimedToday;

  const telegramBotLink = account && TELEGRAM_BOT_USERNAME
    ? `https://t.me/${TELEGRAM_BOT_USERNAME}?start=${account}`
    : undefined;

  const twitterAuthUrl = account
    ? `${BACKEND_URL}/api/twitter/auth?address=${account}`
    : undefined;

  const retweetIntentUrl = tweetId
    ? `https://twitter.com/intent/retweet?tweet_id=${tweetId}`
    : undefined;

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 16 },
        show: { opacity: 1, y: 0 },
      }}
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      className={cx(
        "relative flex flex-col gap-10 overflow-hidden rounded-8 border-1/2 bg-slate-800 p-14 transition-colors",
        claimed ? "border-green-500/40" : "border-slate-600 hover:border-slate-500"
      )}
    >
      <AnimatePresence>
        {flash === "success" && (
          <motion.div
            key="ok"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-green-500/15 backdrop-blur-[1px]"
          >
            <motion.div
              initial={{ scale: 0.6, rotate: -10 }}
              animate={{ scale: 1.05, rotate: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 14 }}
              className="rounded-full bg-green-500 px-16 py-6 text-h2 font-bold text-slate-900 shadow-[0_0_30px_rgba(15,222,141,0.6)]"
            >
              ✓
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-start justify-between gap-8">
        <div className="min-w-0">
          <span className={cx("rounded-full px-8 py-2 text-caption uppercase", meta.color)}>
            {meta.label}
          </span>
          <h4 className="mt-8 truncate text-body-large font-bold text-white">{quest.title}</h4>
          <p className="mt-2 text-body-small leading-snug text-slate-100 opacity-70">
            {quest.description}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-6 text-caption">
        <Reward icon="✦" value={`${quest.reward.xp} XP`} />
        {!!quest.reward.raffleTickets && <Reward icon="🎟" value={`${quest.reward.raffleTickets}`} />}
        {!!quest.reward.letters?.length && <Reward icon="🔤" value={quest.reward.letters.join(" ")} />}
      </div>

      {/* Twitter quest: embed tweet + connect + repost + verify */}
      {isTwitter && !claimed && (
        <div className="flex flex-col gap-8">
          {/* Embedded tweet (compact, scrollable if long) */}
          {tweetId && (
            <div
              data-theme="dark"
              className="tweet-embed-compact max-h-[220px] overflow-y-auto overflow-x-hidden rounded-8 border-1/2 border-slate-700 bg-slate-900/60"
            >
              <Tweet id={tweetId} />
            </div>
          )}

          {/* Connect Twitter */}
          {!twitterLinked.connected ? (
            <a
              href={twitterAuthUrl}
              className={cx(
                "flex items-center justify-center gap-6 rounded-full px-14 py-8 text-body-small font-semibold transition-colors",
                "bg-black text-white border-1/2 border-slate-600 hover:border-slate-400"
              )}
            >
              <XIcon />
              <Trans>Connect X</Trans>
            </a>
          ) : (
            <span className="flex items-center justify-center gap-6 text-body-small text-green-500">
              <XIcon />
              <Trans>Connected as @{twitterLinked.username}</Trans>
            </span>
          )}

          {/* Repost button */}
          {retweetIntentUrl && (
            <a
              href={retweetIntentUrl}
              target="_blank"
              rel="noreferrer"
              className={cx(
                "flex items-center justify-center gap-6 rounded-full px-14 py-8 text-body-small font-semibold transition-colors",
                "bg-[#1D9BF0] text-white hover:bg-[#1A8CD8]"
              )}
            >
              <RetweetIcon />
              <Trans>Repost</Trans>
            </a>
          )}
        </div>
      )}

      {/* Telegram quest: Connect via bot (bot adds user to group) + Verify */}
      {isTelegram && !claimed && (
        <div className="flex flex-col gap-8">
          {!telegramLinked ? (
            <a
              href={telegramBotLink}
              target="_blank"
              rel="noreferrer"
              className={cx(
                "flex items-center justify-center gap-6 rounded-full px-14 py-8 text-body-small font-semibold transition-colors",
                "bg-[#2AABEE] text-white hover:bg-[#229ED9]"
              )}
            >
              <TelegramIcon />
              <Trans>Join</Trans>
            </a>
          ) : (
            <span className="flex items-center justify-center gap-6 text-body-small text-green-500">
              <TelegramIcon />
              <Trans>Connected</Trans>
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex justify-end pt-4">
        {(isTelegram || isTwitter) && !claimed ? (
          <motion.button
            type="button"
            whileTap={{ scale: 0.94 }}
            whileHover={!busy ? { scale: 1.03 } : undefined}
            disabled={busy || loading}
            onClick={handleSocialVerify}
            className={cx(
              "rounded-full px-14 py-6 text-caption font-semibold uppercase tracking-wide transition-colors",
              busy
                ? "bg-green-500/60 text-slate-900"
                : "bg-green-500 text-slate-900 hover:bg-[#2ff2a3] shadow-[0_0_12px_rgba(15,222,141,0.35)]"
            )}
          >
            {busy ? <Trans>Verifying…</Trans> : <Trans>Verify</Trans>}
          </motion.button>
        ) : (
          <motion.button
            type="button"
            whileTap={{ scale: 0.94 }}
            whileHover={!claimed && !busy ? { scale: 1.03 } : undefined}
            disabled={claimed || busy || loading}
            onClick={handleClick}
            className={cx(
              "rounded-full px-14 py-6 text-caption font-semibold uppercase tracking-wide transition-colors",
              claimed
                ? "bg-slate-700 text-slate-100 opacity-60"
                : busy
                  ? "bg-green-500/60 text-slate-900"
                  : "bg-green-500 text-slate-900 hover:bg-[#2ff2a3] shadow-[0_0_12px_rgba(15,222,141,0.35)]"
            )}
          >
            {claimed ? <Trans>Claimed ✓</Trans> : busy ? <Trans>Verifying…</Trans> : <Trans>Claim</Trans>}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function RetweetIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2h6v2h-6c-2.209 0-4-1.791-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 6H10.5V4h6c2.209 0 4 1.791 4 4v8.45l2.068-1.93 1.364 1.46-4.432 4.14-4.432-4.14 1.364-1.46 2.068 1.93V8c0-1.1-.896-2-2-2z" />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function Reward({ icon, value }: { icon: string; value: string }) {
  return (
    <span className="rounded-full bg-slate-700 px-8 py-2 text-slate-100">
      {icon} {value}
    </span>
  );
}
